import React, { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, Info, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import EmptyState from './EmptyState';
import analyticsService from '../../services/analyticsService';
import { downloadFile } from '../../utils/downloadFile';
import { POSITION_RETENTION_DAYS } from '../../constants/tradingConstants';
import { useAnalyticsData } from '../../context/AnalyticsDataContext';
import type { PositionsInRangeResponse, PositionInRange } from '../../types/analytics';

const PREVIEW_ROWS = 100;

const fmtInr = (n: number): string => {
  if (n === 0) return '₹0';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const fmtInrSigned = (n: number): string => (n >= 0 ? '+' : '') + fmtInr(n);

const retentionCutoffIso = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - POSITION_RETENTION_DAYS);
  return d.toISOString().slice(0, 10);
};

const COLUMNS: Array<{ key: keyof PositionInRange; label: string }> = [
  { key: 'closedDate',          label: 'Closed' },
  { key: 'broker',              label: 'Broker' },
  { key: 'clientId',            label: 'Client' },
  { key: 'basketName',          label: 'Basket' },
  { key: 'strategyName',        label: 'Strategy' },
  { key: 'symbol',              label: 'Symbol' },
  { key: 'action',              label: 'Side' },
  { key: 'quantity',            label: 'Qty' },
  { key: 'averageEntryPrice',   label: 'Entry' },
  { key: 'exitAveragePrice',    label: 'Exit' },
  { key: 'pnl',                 label: 'P&L' },
  { key: 'exitType',            label: 'Exit Type' },
];

/** Lean CSV exporter specific to the positions-in-range payload. RFC 4180
 *  escaping + UTF-8 BOM so Excel opens it cleanly. ~25 columns covering
 *  everything needed for broker-statement reconciliation. */
function buildCsv(items: PositionInRange[]): string {
  const cols: Array<{ key: keyof PositionInRange; label: string }> = [
    { key: 'positionId', label: 'position_id' },
    { key: 'executionDate', label: 'execution_date' },
    { key: 'closedDate', label: 'closed_date' },
    { key: 'broker', label: 'broker' },
    { key: 'clientId', label: 'client_id' },
    { key: 'basketId', label: 'basket_id' },
    { key: 'basketName', label: 'basket_name' },
    { key: 'strategyId', label: 'strategy_id' },
    { key: 'strategyName', label: 'strategy_name' },
    { key: 'underlying', label: 'underlying' },
    { key: 'symbol', label: 'symbol' },
    { key: 'exchange', label: 'exchange' },
    { key: 'product', label: 'product' },
    { key: 'tradingType', label: 'trading_type' },
    { key: 'intradayExitMode', label: 'intraday_exit_mode' },
    { key: 'action', label: 'action' },
    { key: 'lots', label: 'lots' },
    { key: 'quantity', label: 'quantity' },
    { key: 'entryFilledAt', label: 'entry_filled_at' },
    { key: 'expectedEntryPrice', label: 'expected_entry_price' },
    { key: 'averageEntryPrice', label: 'average_entry_price' },
    { key: 'exitAveragePrice', label: 'exit_average_price' },
    { key: 'pnl', label: 'pnl' },
    { key: 'exitType', label: 'exit_type' },
    { key: 'positionStatus', label: 'position_status' },
    { key: 'entryStatus', label: 'entry_status' },
    { key: 'exitStatus', label: 'exit_status' },
    { key: 'slStatus', label: 'sl_status' },
    { key: 'expiryDate', label: 'expiry_date' },
  ];
  const escape = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = cols.map((c) => c.label).join(',');
  const rows = items.map((p) => cols.map((c) => escape(p[c.key])).join(','));
  // UTF-8 BOM prefix so Excel opens with correct encoding.
  return '﻿' + header + '\n' + rows.join('\n') + '\n';
}

function csvFilename(start: string, end: string): string {
  return `positions_${start}_to_${end}.csv`;
}

const PositionsInRange: React.FC = () => {
  const { filters } = useAnalyticsData();
  const [data, setData] = useState<PositionsInRangeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await analyticsService.getPositionsInRange(filters);
      setData(resp);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load positions.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchPositions(); }, [fetchPositions]);

  const handleDownload = () => {
    if (!data || data.items.length === 0) return;
    const csv = buildCsv(data.items);
    downloadFile(csvFilename(filters.start, filters.end), csv, 'text/csv;charset=utf-8;');
  };

  const cutoff = retentionCutoffIso();
  const rangeStartsBeforeCutoff = filters.start < cutoff;
  const rangeFullyBeforeCutoff = filters.end < cutoff;

  const items = data?.items || [];
  const totalPnl = items.reduce((s, p) => s + (p.pnl || 0), 0);
  const visibleRows = items.slice(0, PREVIEW_ROWS);
  const truncated = data?.truncated ?? false;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>
            Positions ({data?.total ?? 0})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPositions}
              disabled={loading}
              leftIcon={<RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleDownload}
              disabled={!data || data.items.length === 0}
              leftIcon={<Download className="h-4 w-4" />}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Retention banner */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50/40 dark:bg-blue-900/10">
          <div className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <span>
              Positions are kept for <strong>{POSITION_RETENTION_DAYS} days</strong> from close.
              Trades closed before <strong>{cutoff}</strong> have been purged.
              Aggregate KPIs (Top Trades, Distribution, breakdowns) read from precomputed
              rollups and never expire.
            </span>
          </div>
        </div>

        {/* Range-clipped warning */}
        {rangeStartsBeforeCutoff && (
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-amber-50/40 dark:bg-amber-900/10">
            <div className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                {rangeFullyBeforeCutoff
                  ? <>Your selected range ({filters.start} → {filters.end}) is entirely outside the {POSITION_RETENTION_DAYS}-day retention window. No per-trade rows are available.</>
                  : <>Your range starts before {cutoff}. Trades from {filters.start} to {cutoff} have been purged; showing what's left from {cutoff} onwards.</>}
              </span>
            </div>
          </div>
        )}

        {/* Truncation banner */}
        {truncated && data && (
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-amber-50/40 dark:bg-amber-900/10">
            <div className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Showing first {data.cap.toLocaleString('en-IN')} of {data.total.toLocaleString('en-IN')} trades.
                Download includes all {data.cap.toLocaleString('en-IN')}; narrow the date range or filter by account / basket / strategy to see more.
              </span>
            </div>
          </div>
        )}

        {/* Summary strip */}
        {!loading && items.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              Showing {Math.min(visibleRows.length, items.length).toLocaleString('en-IN')} of {data!.total.toLocaleString('en-IN')}
            </span>
            <span>
              <span className="text-gray-500 dark:text-gray-400">Total P&amp;L:</span>{' '}
              <span className={`font-semibold tabular-nums ${totalPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {fmtInrSigned(totalPnl)}
              </span>
            </span>
            <span className="text-gray-500 dark:text-gray-400 text-xs">
              Range: {filters.start} → {filters.end}
            </span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-6 py-4 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="px-6 py-12 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Loading positions...
          </div>
        )}

        {/* Empty */}
        {!loading && !error && items.length === 0 && (
          <div className="px-6 py-8">
            <EmptyState message="No trades closed in this range." />
          </div>
        )}

        {/* Mobile (<md): card list. 12 columns won't fit on phone widths
            even with horizontal scroll, so we render a per-position card
            with the most useful fields surfaced. CSV download remains the
            primary action for full data access. */}
        {!loading && items.length > 0 && (
          <ul className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {visibleRows.map((p) => (
              <li key={p.positionId} className="px-4 py-3 bg-white dark:bg-gray-800">
                {/* Header row: symbol + side + signed P&L (the headline) */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white break-words">
                      {p.symbol}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <span className={p.action === 'BUY' ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-red-600 dark:text-red-400 font-medium'}>
                        {p.action}
                      </span>
                      {' · '}
                      <span className="tabular-nums">{p.quantity}</span>
                      {' · '}
                      {p.exitType}
                    </div>
                  </div>
                  <div className={`shrink-0 text-sm font-semibold tabular-nums ${p.pnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {fmtInrSigned(p.pnl)}
                  </div>
                </div>

                {/* Meta row: closed date + broker/client + entry/exit prices */}
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">Closed</dt>
                    <dd className="text-gray-700 dark:text-gray-300 tabular-nums mt-0.5">{p.closedDate}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">Account</dt>
                    <dd className="text-gray-700 dark:text-gray-300 mt-0.5">{p.broker} · {p.clientId}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">Entry → Exit</dt>
                    <dd className="text-gray-700 dark:text-gray-300 tabular-nums mt-0.5">
                      {fmtInr(p.averageEntryPrice)} → {fmtInr(p.exitAveragePrice)}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">Basket · Strategy</dt>
                    <dd className="text-gray-700 dark:text-gray-300 mt-0.5 truncate" title={`${p.basketName || p.basketId} · ${p.strategyName || p.strategyId}`}>
                      {p.basketName || p.basketId} · {p.strategyName || p.strategyId}
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
            {items.length > PREVIEW_ROWS && (
              <li className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60">
                Showing first {PREVIEW_ROWS} of {items.length.toLocaleString('en-IN')} rows in the preview.
                Download CSV for the full set.
              </li>
            )}
          </ul>
        )}

        {/* Desktop (md+): full 12-column table */}
        {!loading && items.length > 0 && (
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 whitespace-nowrap"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((p) => (
                  <tr
                    key={p.positionId}
                    className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/60"
                  >
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums text-gray-700 dark:text-gray-300">{p.closedDate}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{p.broker}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{p.clientId}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{p.basketName || p.basketId}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{p.strategyName || p.strategyId}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{p.symbol}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={p.action === 'BUY' ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-red-600 dark:text-red-400 font-medium'}>
                        {p.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-700 dark:text-gray-300">{p.quantity}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtInr(p.averageEntryPrice)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtInr(p.exitAveragePrice)}</td>
                    <td className={`px-4 py-3 whitespace-nowrap text-right tabular-nums font-semibold ${p.pnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                      {fmtInrSigned(p.pnl)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{p.exitType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length > PREVIEW_ROWS && (
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                Showing first {PREVIEW_ROWS} of {items.length.toLocaleString('en-IN')} rows in the preview.
                Download CSV for the full set.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PositionsInRange;
